import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useFieldArray } from "react-hook-form";
import {
  Box,
  Checkbox,
  FormHelperText,
  FormLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core";

import { makeKey } from "../../../../utils/helpers";

import { useFieldsStyles } from "../fields.styles";
import { useSelectFieldStyles } from "../select-field/select-field.styles";

import { SelectFieldArrayProps } from "./select-field-array.props";

export const SelectFieldArray = (props: SelectFieldArrayProps) => {
  const { FieldArrayField, fullWidth = false } = props;

  const commonClasses = useFieldsStyles();
  const selectClasses = useSelectFieldStyles();

  const fieldValue = props.form.getValues(props.name);
  const [selection, setSelection] = useState<string[]>([]);
  const selectionRef = useRef(selection);

  useEffect(() => {
    const newSelection = (fieldValue ?? []).map((field: any) => field[props.fieldKey]);
    setSelection(newSelection);
    selectionRef.current = newSelection;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const { fields, append, remove } = useFieldArray({
    control: props.form.control,
    keyName: "key",
    name: `${props.name}` as const,
    shouldUnregister: false,
  });

  const renderValue = () => props.placeholder ?? "";

  const hasError = !!props.form.formState.errors[props.name];
  const errorMessage = props.form.formState.errors[props.name]?.message;
  const canEdit = Boolean(props.userPermissions?.canEdit) || Boolean(props.noAuthRequired);

  const handleSelect = (event: ChangeEvent<any>) => {
    if (selectionRef.current.length > event.target.value.length) {
      const removed = selectionRef.current.find(
        (type: string) => !event.target.value.includes(type)
      );
      remove(selectionRef.current.indexOf(removed!));
    } else {
      const added = event.target.value.find(
        (option: string) => !selectionRef.current.includes(option)
      );
      append(props.appendField(added));
    }

    setSelection(event.target.value);
    selectionRef.current = event.target.value;

    return props.form.trigger();
  };

  return (
    <div className={commonClasses.fieldWrapper}>
      <Box display="flex" justifyContent="space-between">
        <FormLabel htmlFor={props.name} error={hasError} required={props.required}>
          {props.label}
        </FormLabel>
      </Box>
      <Select
        disabled={!canEdit}
        displayEmpty
        error={hasError}
        id={props.name}
        fullWidth={fullWidth}
        multiple
        onChange={handleSelect}
        renderValue={renderValue}
        value={selection}
        variant="outlined"
      >
        {props.options.map((option, index) => (
          <MenuItem key={makeKey(props.name, index)} value={option.value}>
            <Checkbox
              className={selectClasses.checkbox}
              checked={selection.indexOf(String(option.value)) > -1}
            />
            <ListItemText className={selectClasses.option} primary={option.label} />
          </MenuItem>
        ))}
      </Select>

      <div className={props.fieldArrayFieldWrapperClass}>
        {fields.map((field: any, index) => {
          return (
            <FieldArrayField
              {...props}
              key={makeKey(field.key, index)}
              field={field}
              index={index}
              remove={remove}
            />
          );
        })}
      </div>

      {hasError && <FormHelperText error={hasError}>{errorMessage}</FormHelperText>}
    </div>
  );
};
